import React, { useEffect } from 'react';
import { Col, ModalBody, Row } from 'reactstrap';
import { useSelector } from 'react-redux';
import { Controller, useForm } from 'react-hook-form';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { ModalHeader } from 'shared/components/modal/ModalHeaders';
import { LabelRequiredInput, LabelError } from 'shared/components/Labels';
import { TextField } from 'shared/components/form/Inputs';
import { Button } from 'shared/components/Buttons';
import DropdownSearch from 'shared/components/DropdownSearch';

import { CustomFolderProps } from 'shared/prop-types/ReducerProps';
import { colors } from 'shared/defaultTheme';
import { cleanSpaceString } from 'shared/utils';

import { CustomModalWrapper } from './styles';

const SubfolderFormModal = ({ selectedSubfolder, isEdit, rootFolders, onCloseModal, onSubmit }) => {
  const { t } = useTranslation();
  const { selectedDefaultLanguage } = useSelector((state) => state.topBar);
  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {
      name: isEdit ? selectedSubfolder.name : '',
      pathId: isEdit ? selectedSubfolder.pathId : selectedSubfolder?.id,
    },
  });

  const getNameSelectedLanguage = (i18List) => {
    if (i18List) {
      const findName = i18List.find((x) => x.i18Code === selectedDefaultLanguage);
      if (findName && findName?.i18Name !== '') {
        return findName?.i18Name;
      }
      return i18List[0].i18Name;
    }
    return null;
  };

  const getParentId = (pathId) => {
    const pathIds = pathId.split('/');
    const clearedPathIds = pathIds.filter((x) => x.length > 0);
    const parentId = clearedPathIds[clearedPathIds.length - 2];
    return parentId;
  };

  useEffect(() => {
    if (isEdit) {
      setValue('name', getNameSelectedLanguage(selectedSubfolder.i18List));
      setValue('pathId', getParentId(selectedSubfolder.pathId));
    }
  }, [isEdit]);

  const onCancel = () => {
    setValue('name', isEdit ? selectedSubfolder.name : '');
    setValue('pathId', isEdit ? selectedSubfolder.pathId : '');
    onCloseModal();
  };

  const Title = () => (
    <Row className="row-title">
      <Col sm={12}>
        <h3>{t(isEdit ? 'custom_folder_edit_subfolder' : 'custom_folder_new_subfolder')}</h3>
      </Col>
    </Row>
  );

  return (
    <CustomModalWrapper isOpen toggle={onCloseModal} size="md">
      <ModalHeader
        className="modal-header"
        backgroundColor={colors.transparent}
        onClose={onCloseModal}
      />
      <ModalBody>
        <Title />
        <Row className="row-content w-100">
          <Col sm={12}>
            <form className="form" noValidate onSubmit={handleSubmit(onSubmit)}>
              <Row className="w-100 remove-margin-horizontal">
                <Col sm={12}>
                  <div className="form__form-group">
                    <LabelRequiredInput
                      className="form__form-group-label"
                      label={t('general_name')}
                      color={colors.black}
                      error={errors.name !== undefined}
                    />
                    <Controller
                      name="name"
                      control={control}
                      defaultValue=""
                      rules={{
                        required: { value: true, message: t('general_required_field') },
                      }}
                      render={({ field }) => (
                        <TextField
                          value={field.value}
                          onChange={(e) => {
                            const newEvent = cleanSpaceString(e);
                            field.onChange(newEvent);
                          }}
                          onBlur={field.onBlur}
                          error={errors.name !== undefined}
                        />
                      )}
                    />
                    {errors.name && <LabelError label={errors.name.message} />}
                  </div>
                </Col>
              </Row>
              <Row className="w-100 remove-margin-horizontal">
                <Col sm={12}>
                  <div className="form__form-group">
                    <LabelRequiredInput
                      className="form__form-group-label"
                      label={t('custom_folder_root_folder')}
                      color={colors.black}
                      error={errors.pathId !== undefined}
                    />
                    <Controller
                      name="pathId"
                      control={control}
                      defaultValue=""
                      rules={{
                        required: { value: true, message: t('general_required_field') },
                      }}
                      render={({ field }) => (
                        <DropdownSearch
                          id="sub-folder-form-dropdown-search"
                          options={rootFolders.map((folder) => ({
                            name: getNameSelectedLanguage(folder.i18List),
                            value: folder.id,
                          }))}
                          placeholder=""
                          value={field.value}
                          onBlur={field.onBlur}
                          onChange={field.onChange}
                          isDisabled={isEdit}
                          error={errors.pathId !== undefined}
                        />
                      )}
                    />
                    {errors.pathId && <LabelError label={errors.pathId.message} />}
                  </div>
                </Col>
              </Row>
              <Row className="w-100 remove-margin-horizontal">
                <Col className="d-flex flex-row justify-content-end" sm={12}>
                  <Button
                    className="text-uppercase mr-4"
                    type="button"
                    color="secondary"
                    label={t('general_button_cancel')}
                    onClick={onCancel}
                  />
                  <Button
                    className="text-uppercase"
                    type="submit"
                    color="primary"
                    label={t(isEdit ? 'general_button_update' : 'general_add')}
                  />
                </Col>
              </Row>
            </form>
          </Col>
        </Row>
      </ModalBody>
    </CustomModalWrapper>
  );
};

SubfolderFormModal.defaultProps = {
  selectedSubfolder: undefined,
  isEdit: false,
};

SubfolderFormModal.propTypes = {
  selectedSubfolder: CustomFolderProps,
  isEdit: PropTypes.bool,
  rootFolders: PropTypes.arrayOf(CustomFolderProps).isRequired,
  onCloseModal: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

export default SubfolderFormModal;
